import React from 'react';

/**
 * Componente Badge para mostrar etiquetas o indicadores de estado
 * 
 * @param {Object} props - Propiedades del componente
 * @param {ReactNode} props.children - Contenido del badge
 * @param {string} props.variant - Variante del badge ('success', 'warning', 'danger', 'info', 'primary', 'secondary')
 * @param {string} props.size - Tamaño del badge ('sm', 'md', 'lg')
 * @param {boolean} props.pill - Si el badge debe tener forma de píldora
 * @param {boolean} props.outline - Si el badge debe tener estilo de contorno
 * @param {string} props.className - Clases adicionales
 * @returns {JSX.Element} - Componente Badge
 */
const Badge = ({
  children,
  variant = 'primary',
  size = 'md',
  pill = true,
  outline = false,
  className = '',
  ...rest
}) => {
  // Mapeo de variantes a colores
  const getVariantClasses = () => {
    if (outline) {
      switch (variant) {
        case 'success':
          return 'text-green-500 border border-green-500 bg-transparent';
        case 'warning':
          return 'text-yellow-500 border border-yellow-500 bg-transparent';
        case 'danger':
          return 'text-red-500 border border-red-500 bg-transparent';
        case 'info':
          return 'text-purple-500 border border-purple-500 bg-transparent';
        case 'primary':
          return 'text-blue-500 border border-blue-500 bg-transparent';
        case 'secondary':
          return 'text-gray-400 border border-gray-500 bg-transparent';
        default:
          return 'text-blue-500 border border-blue-500 bg-transparent';
      }
    } else {
      switch (variant) {
        case 'success':
          return 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900 dark:text-green-100 dark:border-green-700';
        case 'warning':
          return 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-700';
        case 'danger':
          return 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900 dark:text-red-100 dark:border-red-700';
        case 'info':
          return 'bg-purple-100 text-purple-800 border border-purple-200 dark:bg-purple-900 dark:text-purple-100 dark:border-purple-700';
        case 'primary':
          return 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700';
        case 'secondary':
          return 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700';
        default:
          return 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700';
      }
    }
  };

  // Mapeo de tamaños
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-1.5 py-0.5';
      case 'lg':
        return 'text-sm px-3 py-1';
      case 'md':
      default:
        return 'text-xs px-2.5 py-0.5';
    }
  };

  const classes = `
    inline-flex items-center font-medium
    ${getVariantClasses()}
    ${getSizeClasses()}
    ${pill ? 'rounded-full' : 'rounded'}
    ${className}
  `;

  return (
    <span className={classes.trim()} {...rest}>
      {children}
    </span>
  );
};

export default Badge;